/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/activity/activityFeedCallDisplay.ts
 * PURPOSE: Format call duration and map CRM call **`metadata.outcome`** to feed badge copy and tone
 * DEPENDENCIES: ../../types/activity.types
 * PUBLIC_API: formatCallDuration, callOutcomeBadge, callOutcomeTonePillClassName, type CallOutcomeTone
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 57/150
 */

import type { Activity } from '../../types/activity.types';

export function formatCallDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m <= 0) return `${r}s`;
  return r > 0 ? `${m}m ${r}s` : `${m}m`;
}

export type CallOutcomeTone = 'success' | 'warning' | 'neutral' | 'danger' | 'info';

export function callOutcomeBadge(activity: Activity): { label: string; tone: CallOutcomeTone } | null {
  const raw = (activity.metadata?.outcome ?? activity.outcome ?? '').toLowerCase().trim();
  if (!raw) return null;
  if (raw === 'completed' || raw.includes('completed')) {
    return { label: 'Completed', tone: 'success' };
  }
  if (raw === 'follow-up-required' || raw.includes('follow-up') || raw.includes('follow up')) {
    return { label: 'Follow-up', tone: 'warning' };
  }
  if (raw === 'no-answer' || raw.includes('no answer') || raw.includes('no-answer')) {
    return { label: 'No answer', tone: 'danger' };
  }
  if (raw === 'left-voicemail' || raw.includes('voicemail')) {
    return { label: 'Left voicemail', tone: 'info' };
  }
  const display = activity.metadata?.outcome ?? activity.outcome;
  return display ? { label: display.replace(/-/g, ' '), tone: 'neutral' } : null;
}

/** Tailwind pill classes for **`callOutcomeBadge`** tones — keep in sync with **`ActivityFeedItem`** call row. */
export function callOutcomeTonePillClassName(tone: CallOutcomeTone): string {
  const base = 'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium';
  switch (tone) {
    case 'success':
      return `${base} bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200`;
    case 'warning':
      return `${base} bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100`;
    case 'danger':
      return `${base} bg-red-100 text-red-800 dark:bg-red-900/45 dark:text-red-100`;
    case 'info':
      return `${base} bg-blue-100 text-blue-800 dark:bg-blue-900/45 dark:text-blue-100`;
    case 'neutral':
      return `${base} bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-200`;
  }
}
